import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 150;

/**
 * Text input that keeps what you type in local state and only lifts it to the
 * owner after a short pause.
 *
 * Without this, every keystroke in a /users form field sets state up in
 * UserManager, which re-renders the whole form — all the role pills, the
 * SectionsCard motion columns, the lot — once per character.
 *
 * The pending value is flushed on blur and on unmount, so nothing typed can be
 * lost by clicking Save (the blur lands first) or by removing the card.
 */
const DebouncedTextInput = React.memo(function DebouncedTextInput({
  value,
  onValueChange,
  delay = DEFAULT_DELAY,
  ...props
}) {
  const [local, setLocal] = useState(value ?? '');
  const timerRef = useRef(null);
  // What we last sent up, so an owner re-render doesn't clobber in-flight typing.
  const pushedRef = useRef(value ?? '');
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  // Adopt the owner's value when it changes for a reason other than our own push
  // (a draft being reset, an edit card being seeded).
  useEffect(() => {
    const next = value ?? '';
    if (next !== pushedRef.current) {
      pushedRef.current = next;
      setLocal(next);
    }
  }, [value]);

  const flush = useCallback((next) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (next !== pushedRef.current) {
      pushedRef.current = next;
      onValueChangeRef.current?.(next);
    }
  }, []);

  // Flush whatever is pending if this field goes away mid-edit.
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleChange = (event) => {
    const next = event.target.value;
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      pushedRef.current = next;
      onValueChangeRef.current?.(next);
    }, delay);
  };

  return (
    <input
      {...props}
      value={local}
      onChange={handleChange}
      onBlur={(event) => {
        flush(event.target.value);
        props.onBlur?.(event);
      }}
    />
  );
});

export default DebouncedTextInput;
